'use client'

import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Icon } from '@/components/portfolio/Icon'
import {
  profile,
  stats,
  services,
  skills,
  projects,
  process,
  marqueeItems,
} from '@/components/portfolio/data'

// ============================================================================
// ANIMATION VARIANTS
// ============================================================================
const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] as const },
  },
}

const stagger = {
  visible: { transition: { staggerChildren: 0.1 } },
}

const navLinks = [
  { label: 'Sobre', href: '#sobre' },
  { label: 'Serviços', href: '#servicos' },
  { label: 'Projetos', href: '#projetos' },
  { label: 'Processo', href: '#processo' },
  { label: 'Contato', href: '#contato' },
]

// ============================================================================
// THEME TOGGLE
// ============================================================================
function ThemeToggle(): React.ReactElement {
  const [theme, setTheme] = useState<'light' | 'dark'>('dark')
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
    const saved = localStorage.getItem('theme') as 'light' | 'dark' | null
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches
    const initial = saved ?? (prefersDark ? 'dark' : 'light')
    setTheme(initial)
    document.documentElement.classList.toggle('dark', initial === 'dark')
  }, [])

  const toggle = (): void => {
    const next = theme === 'light' ? 'dark' : 'light'
    setTheme(next)
    document.documentElement.classList.toggle('dark', next === 'dark')
    localStorage.setItem('theme', next)
  }

  if (!mounted) return <div className="w-10 h-10" />

  return (
    <button
      onClick={toggle}
      className="w-10 h-10 rounded-full border border-neutral-200 dark:border-neutral-800 flex items-center justify-center text-neutral-600 dark:text-neutral-300 hover:border-accent-400 hover:text-accent-500 transition-colors"
      aria-label={`Mudar para modo ${theme === 'light' ? 'escuro' : 'claro'}`}
    >
      <Icon name={theme === 'dark' ? 'sun' : 'moon'} className="w-5 h-5" />
    </button>
  )
}

// ============================================================================
// HEADER
// ============================================================================
function Header(): React.ReactElement {
  const [open, setOpen] = useState(false)

  return (
    <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-md bg-white/70 dark:bg-neutral-950/70 border-b border-neutral-100 dark:border-neutral-900">
      <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
        <a href="#topo" className="flex items-center gap-2 font-semibold tracking-tight">
          <span className="w-8 h-8 rounded-lg bg-accent-400 text-neutral-950 flex items-center justify-center">
            <Icon name="sparkle" className="w-4 h-4" />
          </span>
          <span>
            Rulio<span className="text-accent-500">.</span>
          </span>
        </a>

        <nav className="hidden md:flex items-center gap-8 text-sm">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              className="text-neutral-500 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-white transition-colors"
            >
              {link.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <ThemeToggle />
          <a
            href="#contato"
            className="hidden sm:inline-flex px-4 py-2 text-sm font-medium bg-neutral-900 dark:bg-white text-white dark:text-neutral-900 rounded-full hover:bg-accent-500 hover:text-neutral-950 dark:hover:bg-accent-400 transition-colors"
          >
            Vamos conversar
          </a>
          <button
            onClick={() => setOpen((v) => !v)}
            className="md:hidden w-10 h-10 rounded-full border border-neutral-200 dark:border-neutral-800 flex items-center justify-center"
            aria-label="Abrir menu"
          >
            <div className="space-y-1.5">
              <span className="block w-5 h-0.5 bg-current" />
              <span className="block w-5 h-0.5 bg-current" />
            </div>
          </button>
        </div>
      </div>

      {open && (
        <nav className="md:hidden border-t border-neutral-100 dark:border-neutral-900 px-6 py-4 flex flex-col gap-4 text-sm">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={() => setOpen(false)}
              className="text-neutral-600 dark:text-neutral-300"
            >
              {link.label}
            </a>
          ))}
        </nav>
      )}
    </header>
  )
}

// ============================================================================
// HERO
// ============================================================================
function Hero(): React.ReactElement {
  return (
    <section id="topo" className="relative pt-36 pb-24 px-6 overflow-hidden">
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.03] dark:opacity-[0.06]"
        style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, currentColor 1px, transparent 0)',
          backgroundSize: '38px 38px',
        }}
      />
      <div
        className="absolute -top-32 -right-32 w-[32rem] h-[32rem] rounded-full pointer-events-none blur-3xl opacity-30 dark:opacity-20"
        style={{ background: 'radial-gradient(circle, #84cf3a, transparent 70%)' }}
      />

      <motion.div
        initial="hidden"
        animate="visible"
        variants={stagger}
        className="max-w-6xl mx-auto relative z-10"
      >
        <motion.div
          variants={fadeUp}
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-neutral-200 dark:border-neutral-800 text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-8"
        >
          <span className="w-2 h-2 rounded-full bg-accent-400 animate-pulse" />
          Disponível para novos projetos
        </motion.div>

        <motion.h1
          variants={fadeUp}
          className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-semibold tracking-tighter leading-[0.95] mb-8"
        >
          {profile.name.split(' ')[0]}{' '}
          <span className="text-accent-500">{profile.name.split(' ')[1]}</span>
          <br />
          <span className="text-neutral-400 dark:text-neutral-600">{profile.role}</span>
        </motion.h1>

        <motion.p
          variants={fadeUp}
          className="max-w-xl text-lg sm:text-xl text-neutral-600 dark:text-neutral-400 mb-10 leading-relaxed"
        >
          {profile.headline}
        </motion.p>

        <motion.div variants={fadeUp} className="flex flex-col sm:flex-row gap-4">
          <a
            href="#projetos"
            className="group inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-neutral-900 dark:bg-white text-white dark:text-neutral-900 font-medium rounded-full hover:bg-accent-500 hover:text-neutral-950 dark:hover:bg-accent-400 transition-colors"
          >
            Ver projetos
            <Icon name="arrow" className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </a>
          <a
            href="#contato"
            className="inline-flex items-center justify-center gap-2 px-7 py-3.5 border border-neutral-300 dark:border-neutral-700 font-medium rounded-full hover:border-accent-400 hover:text-accent-500 transition-colors"
          >
            Entrar em contato
          </a>
        </motion.div>
      </motion.div>
    </section>
  )
}

// ============================================================================
// MARQUEE
// ============================================================================
function Marquee(): React.ReactElement {
  const items = [...marqueeItems, ...marqueeItems]
  return (
    <div className="py-6 border-y border-neutral-100 dark:border-neutral-900 overflow-hidden">
      <div className="flex gap-8 w-max animate-marquee">
        {items.map((item, i) => (
          <div key={i} className="flex items-center gap-8 text-2xl font-medium text-neutral-400 dark:text-neutral-600">
            <span>{item}</span>
            <span className="text-accent-400">✦</span>
          </div>
        ))}
      </div>
    </div>
  )
}

// ============================================================================
// SECTION WRAPPER
// ============================================================================
function SectionHeader({ label, title }: { label: string; title: string }): React.ReactElement {
  return (
    <motion.div
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: '-80px' }}
      variants={stagger}
      className="mb-14"
    >
      <motion.p
        variants={fadeUp}
        className="text-xs font-semibold tracking-[0.3em] uppercase text-accent-500 mb-4"
      >
        {label}
      </motion.p>
      <motion.h2
        variants={fadeUp}
        className="text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight max-w-2xl"
      >
        {title}
      </motion.h2>
    </motion.div>
  )
}

// ============================================================================
// ABOUT
// ============================================================================
function About(): React.ReactElement {
  return (
    <section id="sobre" className="py-24 px-6">
      <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-16 items-center">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-80px' }}
          variants={stagger}
        >
          <motion.p
            variants={fadeUp}
            className="text-xs font-semibold tracking-[0.3em] uppercase text-accent-500 mb-4"
          >
            Sobre
          </motion.p>
          <motion.h2
            variants={fadeUp}
            className="text-3xl sm:text-4xl font-semibold tracking-tight mb-6 leading-tight"
          >
            Design e código na mesma mão.
          </motion.h2>
          <motion.p
            variants={fadeUp}
            className="text-neutral-600 dark:text-neutral-400 leading-relaxed mb-4"
          >
            {profile.intro}
          </motion.p>
          <motion.p
            variants={fadeUp}
            className="text-neutral-600 dark:text-neutral-400 leading-relaxed"
          >
            Como profissional full stack, cuido de todo o ciclo do produto: pesquisa, design de
            interface, desenvolvimento front-end, back-end e deploy. O resultado são produtos
            coesos, rápidos e feitos com atenção aos detalhes.
          </motion.p>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-80px' }}
          variants={stagger}
          className="grid grid-cols-2 gap-4"
        >
          {stats.map((stat) => (
            <motion.div
              key={stat.label}
              variants={fadeUp}
              className="p-6 rounded-2xl border border-neutral-100 dark:border-neutral-900 bg-neutral-50 dark:bg-neutral-900/40"
            >
              <div className="text-4xl font-semibold tracking-tight text-accent-500 mb-2">
                {stat.value}
              </div>
              <div className="text-sm text-neutral-500 dark:text-neutral-400">{stat.label}</div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  )
}

// ============================================================================
// SERVICES
// ============================================================================
function Services(): React.ReactElement {
  return (
    <section id="servicos" className="py-24 px-6 bg-neutral-50 dark:bg-neutral-900/30">
      <div className="max-w-6xl mx-auto">
        <SectionHeader label="Serviços" title="O que eu faço para o seu produto crescer." />
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-80px' }}
          variants={stagger}
          className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4"
        >
          {services.map((service) => (
            <motion.div
              key={service.title}
              variants={fadeUp}
              className="group p-6 rounded-2xl bg-white dark:bg-neutral-950 border border-neutral-100 dark:border-neutral-900 hover:border-accent-400 transition-colors"
            >
              <div className="w-12 h-12 rounded-xl bg-accent-100 dark:bg-accent-950 text-accent-600 dark:text-accent-400 flex items-center justify-center mb-5 group-hover:bg-accent-400 group-hover:text-neutral-950 transition-colors">
                <Icon name={service.icon} className="w-6 h-6" />
              </div>
              <h3 className="font-semibold mb-2">{service.title}</h3>
              <p className="text-sm text-neutral-500 dark:text-neutral-400 leading-relaxed">
                {service.description}
              </p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  )
}

// ============================================================================
// PROJECTS
// ============================================================================
function Projects(): React.ReactElement {
  return (
    <section id="projetos" className="py-24 px-6">
      <div className="max-w-6xl mx-auto">
        <SectionHeader label="Portfólio" title="Projetos selecionados." />
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-80px' }}
          variants={stagger}
          className="grid md:grid-cols-2 gap-6"
        >
          {projects.map((project, i) => (
            <motion.article
              key={project.title}
              variants={fadeUp}
              className="group relative p-8 rounded-3xl border border-neutral-100 dark:border-neutral-900 bg-neutral-50 dark:bg-neutral-900/40 hover:bg-white dark:hover:bg-neutral-900 hover:shadow-xl hover:shadow-neutral-200/50 dark:hover:shadow-black/40 transition-all overflow-hidden"
            >
              <div
                className="absolute -right-16 -top-16 w-40 h-40 rounded-full blur-2xl opacity-0 group-hover:opacity-40 transition-opacity"
                style={{ background: 'radial-gradient(circle, #84cf3a, transparent 70%)' }}
              />
              <div className="relative z-10">
                <div className="flex items-center justify-between mb-6">
                  <span className="text-xs font-semibold tracking-widest uppercase text-accent-500">
                    {project.category}
                  </span>
                  <span className="text-sm text-neutral-400">{project.year}</span>
                </div>
                <div className="text-6xl font-semibold tracking-tighter text-neutral-200 dark:text-neutral-800 mb-6">
                  {String(i + 1).padStart(2, '0')}
                </div>
                <h3 className="text-2xl font-semibold tracking-tight mb-3 group-hover:text-accent-500 transition-colors">
                  {project.title}
                </h3>
                <p className="text-neutral-500 dark:text-neutral-400 leading-relaxed mb-6">
                  {project.description}
                </p>
                <div className="flex flex-wrap gap-2">
                  {project.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 text-xs font-medium rounded-full border border-neutral-200 dark:border-neutral-800 text-neutral-600 dark:text-neutral-400"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </motion.article>
          ))}
        </motion.div>
      </div>
    </section>
  )
}

// ============================================================================
// SKILLS
// ============================================================================
function Skills(): React.ReactElement {
  return (
    <section className="py-24 px-6 bg-neutral-50 dark:bg-neutral-900/30">
      <div className="max-w-6xl mx-auto">
        <SectionHeader label="Stack" title="Ferramentas e tecnologias." />
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-80px' }}
          variants={stagger}
          className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8"
        >
          {skills.map((group) => (
            <motion.div key={group.label} variants={fadeUp}>
              <h3 className="text-sm font-semibold tracking-widest uppercase text-neutral-400 mb-4 pb-4 border-b border-neutral-200 dark:border-neutral-800">
                {group.label}
              </h3>
              <ul className="space-y-3">
                {group.items.map((item) => (
                  <li key={item} className="flex items-center gap-3 text-neutral-700 dark:text-neutral-300">
                    <span className="w-1.5 h-1.5 rounded-full bg-accent-400" />
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  )
}

// ============================================================================
// PROCESS
// ============================================================================
function Process(): React.ReactElement {
  return (
    <section id="processo" className="py-24 px-6">
      <div className="max-w-6xl mx-auto">
        <SectionHeader label="Processo" title="Como eu trabalho, do briefing ao deploy." />
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-80px' }}
          variants={stagger}
          className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6"
        >
          {process.map((item) => (
            <motion.div key={item.step} variants={fadeUp} className="relative">
              <div className="text-5xl font-semibold tracking-tighter text-accent-400/40 dark:text-accent-500/30 mb-4">
                {item.step}
              </div>
              <h3 className="text-lg font-semibold mb-2">{item.title}</h3>
              <p className="text-sm text-neutral-500 dark:text-neutral-400 leading-relaxed">
                {item.description}
              </p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  )
}

// ============================================================================
// CONTACT / CTA
// ============================================================================
function Contact(): React.ReactElement {
  return (
    <section id="contato" className="py-24 px-6">
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: '-80px' }}
        variants={stagger}
        className="max-w-4xl mx-auto text-center relative rounded-[2.5rem] px-8 py-20 bg-neutral-900 dark:bg-neutral-900 text-white overflow-hidden"
      >
        <div
          className="absolute inset-0 opacity-30 pointer-events-none blur-3xl"
          style={{ background: 'radial-gradient(circle at 50% 0%, #84cf3a, transparent 60%)' }}
        />
        <div className="relative z-10">
          <motion.p
            variants={fadeUp}
            className="text-xs font-semibold tracking-[0.3em] uppercase text-accent-400 mb-6"
          >
            Contato
          </motion.p>
          <motion.h2
            variants={fadeUp}
            className="text-4xl sm:text-5xl md:text-6xl font-semibold tracking-tighter mb-6"
          >
            Vamos criar algo<br />
            <span className="text-accent-400">extraordinário.</span>
          </motion.h2>
          <motion.p
            variants={fadeUp}
            className="text-neutral-400 max-w-lg mx-auto mb-10"
          >
            Tem um projeto em mente? Me conte a sua ideia e vamos transformá-la em realidade.
          </motion.p>
          <motion.a
            variants={fadeUp}
            href={`mailto:${profile.email}`}
            className="group inline-flex items-center gap-3 px-8 py-4 bg-accent-400 text-neutral-950 font-semibold rounded-full hover:bg-white transition-colors"
          >
            <Icon name="mail" className="w-5 h-5" />
            {profile.email}
          </motion.a>
          <motion.div variants={fadeUp} className="flex items-center justify-center gap-6 mt-12">
            {profile.socials.map((social) => (
              <a
                key={social.label}
                href={social.href}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-neutral-400 hover:text-accent-400 transition-colors"
              >
                {social.label}
              </a>
            ))}
          </motion.div>
        </div>
      </motion.div>
    </section>
  )
}

// ============================================================================
// FOOTER
// ============================================================================
function Footer(): React.ReactElement {
  return (
    <footer className="py-10 px-6 border-t border-neutral-100 dark:border-neutral-900">
      <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-neutral-400">
        <p>
          © {new Date().getFullYear()} {profile.name}. Todos os direitos reservados.
        </p>
        <p>{profile.location}</p>
      </div>
    </footer>
  )
}

// ============================================================================
// PORTFOLIO PAGE
// ============================================================================
export function Portfolio(): React.ReactElement {
  return (
    <div className="min-h-screen bg-white dark:bg-neutral-950 text-neutral-900 dark:text-neutral-100">
      <Header />
      <main>
        <Hero />
        <Marquee />
        <About />
        <Services />
        <Projects />
        <Skills />
        <Process />
        <Contact />
      </main>
      <Footer />
    </div>
  )
}
